import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FormField, Button, Icon, Notification, FormFieldMultiline } from "@weirgroup/weir-dls";
import { BucketDataType, EditPartType, NonEscoPartParamsType, ReplacementPartType } from "../../../types/bucketTypes";
import assetsApi from '../../../api/assets.api';
import { BILL_OF_MATERIAL } from "../../../constants/bucket.contants";
import { useDarkMode } from "../../../hooks/use-darkmode";
import { useQueryClient } from "@tanstack/react-query";
import { useAnalyticsEvent } from "../../../hooks/useAnalyticsEvent";
import { LoadingSpinner } from "../../loading-spinner";

interface Props {
    setShowAddNonEscoPart: Dispatch<SetStateAction<boolean>>;
    setActiveTabIndex: Dispatch<SetStateAction<number>>;
    selectedAsset: BucketDataType;
    setSelectedAsset: Dispatch<SetStateAction<any>>;
    editPart?: ReplacementPartType;
    fetchBucketList?: (arg?: boolean) => Promise<void>;
    onCancel?: () => void;
}

const useFormState = (editPart?: ReplacementPartType) => {
    const [pmid, setPMID] = useState<string>(editPart ? editPart.pmid : "");
    const [description, setDescription] = useState<string>(editPart ? editPart.description : "");
    const [quantity, setQuantity] = useState<number>(editPart ? editPart.quantity : 0);
    const [partNumber, setPartNumber] = useState<string>(editPart ? editPart.part_number : "");
    const [notes, setNotes] = useState<string>(editPart ? editPart.notes : "");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showToast, setShowToast] = useState<boolean>(false);
    const [toastData, setToastData] = useState<any>();
    const [showError, setShowError] = useState<boolean>(false);

    return {
        pmid, setPMID, description, setDescription, quantity, setQuantity,
        partNumber, setPartNumber, notes, setNotes, isLoading, setIsLoading,
        showToast, setShowToast, toastData, setToastData, showError, setShowError
    };
}

const useErrorHandling = (description: string, quantity: number, partNumber: string, setShowError: Dispatch<SetStateAction<boolean>>) => {
    const isNotComplete = (data: any) => {
        // Only require description, quantity, and partNumber
        const [description, quantity, partNumber] = data;
        return description === '' || quantity == 0 || partNumber === '';
    };
    useEffect(() => {
        const data = [description, quantity, partNumber];
        setShowError(isNotComplete(data));
    }, [description, quantity, partNumber]);
    return isNotComplete;
}

const useToastHandling = (showToast: boolean, setShowToast: Dispatch<SetStateAction<boolean>>) => {
    useEffect(() => {
        if (showToast) {
            setTimeout(() => setShowToast(false), 3000);
        }
    }, [showToast]);
};

const InputGroup = ({ darkMode, label, value, onChange, placeholder, type = "text", required = true, min }: any) => (
    <div className="FormFieldPadding">
        <FormField
            darkMode={darkMode}
            name="InputBox"
            required={required}
            label={label}
            placeholder={placeholder}
            onChange={onChange}
            defaultValue={value}
            type={type}
            min={min}
            applyModalInternalGrid
            modalType="large"
            onKeyDown={(e: any) => type === "number" && ["e", "E", "+", "-", "."].includes(e.key) && e.preventDefault()}
        />
    </div>
);

export const AddBillOfMaterial = (props: Props) => {
    const logAnalyticsEvent = useAnalyticsEvent();
    const queryClient = useQueryClient();
    const {
        setShowAddNonEscoPart, setActiveTabIndex, selectedAsset, setSelectedAsset,
        editPart, fetchBucketList, onCancel
    } = props;

    const { t } = useTranslation();
    const { darkMode } = useDarkMode(); // Store darkMode in a variable
    const {
        pmid, setPMID, description, setDescription, quantity, setQuantity,
        partNumber, setPartNumber, notes, setNotes, isLoading, setIsLoading,
        showToast, setShowToast, toastData, setToastData, showError, setShowError
    } = useFormState(editPart);

    const isNotComplete = useErrorHandling(description, quantity, partNumber, setShowError);
    useToastHandling(showToast, setShowToast);

    const goBack = () => {
        setIsLoading(false);
        if (onCancel) {
            onCancel();
        } else {
            setShowAddNonEscoPart(false);
            setActiveTabIndex(1);
        }
    };

    const showErrorMessage = (message?: string) => {
        setToastData({ message, variant: "critical" });
        setShowToast(true);
        setIsLoading(false);
    };

    const handleApiCall = (apiCall: Promise<any>, successCallback: (result: any) => void) => {
        apiCall.then(successCallback).catch(() => {
            showErrorMessage(t("notificationTitleError"));
            goBack();
        });
    };

    const addPart = () => {
        setIsLoading(true);
        if (editPart) {
            const editData: EditPartType = {
                part_id: editPart.part_id,
                pmid: pmid || editPart.pmid,
                description: description || editPart.description,
                quantity: quantity ?? editPart.quantity,
                part_number: partNumber || editPart.part_number,
                notes,
                type: BILL_OF_MATERIAL
            };

            if (!showError) {
                handleApiCall(assetsApi.editReplacementPart(editData), () => {
                    queryClient.invalidateQueries({ queryKey: ["getAsset", selectedAsset.asset_id] });
                    if (fetchBucketList) fetchBucketList(true);
                    goBack();
                });
                logAnalyticsEvent('web_user_edited_part_in_bill_of_material', { description: 'User edited part in bill of material', assetId: selectedAsset.asset_id });
            } else {
                showErrorMessage(t("addPartValidationMessage"));
            }
        } else {
            // Build addData and remove empty fields
            const rawData: NonEscoPartParamsType = {
                asset_id: selectedAsset.asset_id,
                pmid, description, quantity, part_number: partNumber, notes, type: BILL_OF_MATERIAL
            };
            const addData: any = {};
            Object.keys(rawData).forEach(key => {
                const value = (rawData as any)[key];
                if (value !== undefined && value !== null && value !== "") {
                    addData[key] = value;
                }
            });

            if (!isNotComplete([description, quantity, partNumber])) {
                handleApiCall(assetsApi.addNonEscoPart(addData), (result: any) => {
                    queryClient.invalidateQueries({ queryKey: ["getAsset", selectedAsset.asset_id] });
                    setSelectedAsset(result.data.data);
                    goBack();
                });
                logAnalyticsEvent('web_user_added_part_in_bill_of_material', { description: 'User added part in bill of material', assetId: selectedAsset.asset_id });
            } else {
                showErrorMessage(t("addPartValidationMessage"));
            }
        }
    };

    return (
        <div className="add-non-esco">
            {isLoading && (
                <LoadingSpinner darkMode={darkMode} />
            )}
            <button className="title" onClick={goBack} onKeyDown={goBack}>
                <span style={{ color: darkMode ? "rgb(105, 179, 255)" : "#005eb8" }} className="serial_number">{selectedAsset.serial_number}</span>
                <Icon darkMode={darkMode} name={"Right"} />
                <span style={{ color: darkMode ? "rgb(105, 179, 255)" : "#005eb8" }}>{editPart ? t("editPart").toString() : t("addNew").toString()}</span>
            </button>
            <p className="subtitle">{editPart ? t("editNewReplacementPart").toString() : t("addNewReplacementPart").toString()}</p>
            <div className="container">
                <div>
                    <div className="input-group1">
                        <InputGroup
                            darkMode={darkMode}
                            label={t("pmid")}
                            value={editPart ? editPart.pmid : ""}
                            onChange={(e: any) => setPMID(e.target.value)}
                            placeholder={editPart ? editPart.pmid : t("enterNumber")}
                        />
                        <InputGroup
                            darkMode={darkMode}
                            label={t("quantity")}
                            value={editPart ? editPart.quantity : ""}
                            onChange={(e: any) => setQuantity(e.target.value)}
                            placeholder={editPart ? editPart.quantity : t("enterQuantity")}
                            type="number"
                            min="1"
                        />
                    </div>
                </div>
                <div>
                    <div className="input-group2">
                        <InputGroup
                            darkMode={darkMode}
                            label={t("descriptionTitle")}
                            value={editPart ? editPart.description : ""}
                            onChange={(e: any) => setDescription(e.target.value)}
                            placeholder={editPart ? editPart.description : t("enterTitle")}
                        />
                        <InputGroup
                            darkMode={darkMode}
                            label={t("partNumber")}
                            value={editPart ? editPart.part_number : ""}
                            onChange={(e: any) => setPartNumber(e.target.value)}
                            placeholder={editPart ? editPart.part_number : t("enterNumber")}
                        />
                    </div>
                </div>
            </div>
            <div className="multiline-textfield">
                <FormFieldMultiline
                    darkMode={darkMode}
                    errorMessage=""
                    label="Notes"
                    modalType="large"
                    onChange={(e: any) => setNotes(e)}
                    optionalText="Optional"
                    outerContainerPadding="30px"
                    placeholder={editPart ? editPart.notes : "Enter description"}
                    variant="lg"
                />
            </div>
            <div className="btn-container">
                <Button
                    darkMode={darkMode}
                    label={t("cancel")}
                    size="sm"
                    variant="secondary"
                    onClick={goBack}
                />
                <Button
                    darkMode={darkMode}
                    label={editPart ? t("save") : t("addPart")}
                    size="sm"
                    variant="primary"
                    onClick={addPart}
                    disabled={!description || !quantity || quantity === 0 || !partNumber}
                />
            </div>
            {showToast && (
                <div className="toast-container">
                    <Notification
                        darkMode={darkMode}
                        title={toastData.variant == "success" ? "Success" : "Fail"}
                        textContent={toastData.message}
                        variant={toastData.variant}
                        onClose={() => { setShowToast(false); }}
                    />
                </div>
            )}
        </div>
    );
}
